import { MoreHorizontal, ShieldAlert, ShieldCheck, Star, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { MailThread } from "../../../../lib/mail";
import type { CrmLabel } from "../../../../lib/crmLabels";
import { IconBtn, LabelItems } from "../../../ui";
import { Pop, useClosePops } from "../../../shell/Pop";

/**
 * Everything the reading pane's header does rarely.
 *
 * The permanent buttons next to it are picked by frequency, not by danger: an
 * operator archives forty times a day and marks spam twice a week, and giving
 * both the same target would make the common one slower to find. Delete appears
 * in both places on purpose - it has a permanent button because it is the one
 * action people look for by muscle memory, and a menu entry because that is
 * where a reader who is going through the list of options expects it.
 */
export function ThreadMenu({
  thread,
  spam,
  labels,
  onPatch,
  onDelete,
}: {
  thread: MailThread;
  spam: boolean;
  labels: CrmLabel[];
  onPatch: (body: Record<string, unknown>) => Promise<void>;
  onDelete: () => void;
}) {
  const t = useT();
  const close = useClosePops();

  return (
    <Pop
      trigger={(open) => (
        <IconBtn className={cn(open && "is-on")} aria-label={t("common.more")} aria-expanded={open}>
          <MoreHorizontal className="size-4" />
        </IconBtn>
      )}
    >
      <button
        type="button"
        className={cn("crm-mi", thread.starred && "is-on")}
        onClick={() => {
          close();
          void onPatch({ starred: !thread.starred });
        }}
      >
        <Star className={cn("size-4", thread.starred && "fill-amber-400 text-amber-400")} />
        {t("email.star")}
      </button>
      <button
        type="button"
        className="crm-mi"
        onClick={() => {
          close();
          void onPatch({ status: spam ? "open" : "spam" });
        }}
      >
        {spam ? <ShieldCheck className="size-4" /> : <ShieldAlert className="size-4" />}
        {t(spam ? "email.notSpam" : "email.markSpam")}
      </button>

      {/* The menu stays open through a label click: labelling is the one action
          here an operator does more than once in a row ("complaint, needs a
          reply"), and closing after each would mean reopening to finish. */}
      <LabelItems
        title={t("mail.labels")}
        labels={labels}
        tags={thread.tags ?? []}
        onApply={(next) => onPatch({ tags: next })}
      />

      <div className="crm-menu-sep" />
      <button
        type="button"
        className="crm-mi crm-mi-danger"
        onClick={() => {
          close();
          onDelete();
        }}
      >
        <Trash2 className="size-4" />
        {t("email.delete")}
      </button>
    </Pop>
  );
}
